import { useEffect, useMemo, useState } from "react";
import { Search, Save, CheckCircle2 } from "lucide-react";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";

// Inventory: per-product price + in_stock toggle. Per-dose pricing editor inline.
export default function AdminInventory() {
    const [products, setProducts] = useState([]);
    const [query, setQuery] = useState("");
    const [edits, setEdits] = useState({}); // id -> { price?, in_stock?, pricing?, featured? }
    const [savingId, setSavingId] = useState(null);
    const [savedId, setSavedId] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/products")
            .then((r) => setProducts(r.data))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return products;
        return products.filter((p) =>
            [p.name, p.slug, p.short_name, p.category].some((v) => (v || "").toLowerCase().includes(q))
        );
    }, [products, query]);

    const stage = (id, patch) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    const save = async (product) => {
        const patch = edits[product.id];
        if (!patch) return;
        setSavingId(product.id);
        setError("");
        try {
            const { data } = await api.put(`/admin/products/${product.id}`, patch);
            setProducts((prev) => prev.map((p) => (p.id === product.id ? data : p)));
            setEdits((prev) => {
                const next = { ...prev };
                delete next[product.id];
                return next;
            });
            setSavedId(product.id);
            setTimeout(() => setSavedId((cur) => (cur === product.id ? null : cur)), 1800);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-inventory">
            <div className="mb-6 sm:mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">// Inventory</div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                    Stock & pricing
                </h1>
                <p className="text-sm text-neutral-500 mt-2">
                    Toggle in-stock, adjust prices, and override per-dose pricing inline. Changes
                    persist across deployments (admin-edited products are not overwritten by the seed).
                </p>
            </div>

            <div className="relative max-w-md mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, slug, or category"
                    className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg pl-10 pr-3 py-2.5 text-sm"
                    data-testid="inventory-search"
                />
            </div>

            {error && (
                <div className="border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3 mb-4 rounded-lg" data-testid="inventory-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-neutral-500 text-sm">Loading inventory…</div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((p) => {
                        const draft = edits[p.id] || {};
                        const inStock = draft.in_stock ?? p.in_stock;
                        const price = draft.price ?? p.price;
                        const featured = draft.featured ?? p.featured;
                        const pricing = draft.pricing ?? p.pricing ?? null;
                        const dirty = Boolean(edits[p.id]);
                        return (
                            <div
                                key={p.id}
                                className="border border-neutral-200 rounded-lg p-3 sm:p-4 grid grid-cols-[64px_1fr] sm:grid-cols-[80px_1fr] lg:grid-cols-[80px_2fr_1fr_1fr_1fr_140px] gap-3 lg:gap-4 items-start lg:items-center"
                                data-testid={`inv-row-${p.slug}`}
                            >
                                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white border border-neutral-200 rounded-md overflow-hidden">
                                    <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm sm:text-base truncate">{p.name}</div>
                                    <div className="text-xs text-neutral-500 truncate">
                                        {p.category} · {p.slug}
                                    </div>
                                    {p.admin_edited && (
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0077b6] mt-1">
                                            admin-edited
                                        </div>
                                    )}
                                </div>
                                <div className="col-span-2 lg:col-span-1">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">Base price</div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={price}
                                        onChange={(e) => stage(p.id, { price: parseFloat(e.target.value) || 0 })}
                                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm font-mono"
                                        data-testid={`inv-price-${p.slug}`}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">In stock</div>
                                    <button
                                        type="button"
                                        onClick={() => stage(p.id, { in_stock: !inStock })}
                                        className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-lg w-full ${
                                            inStock
                                                ? "bg-emerald-500/10 text-emerald-700 border-emerald-400/30"
                                                : "bg-red-500/10 text-red-700 border-red-400/30"
                                        }`}
                                        data-testid={`inv-stock-${p.slug}`}
                                    >
                                        {inStock ? "in stock" : "out of stock"}
                                    </button>
                                </div>
                                <div className="col-span-1">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">Featured</div>
                                    <button
                                        type="button"
                                        onClick={() => stage(p.id, { featured: !featured })}
                                        className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-lg w-full ${
                                            featured
                                                ? "bg-neutral-100 text-neutral-900 border-[#00aeef]/30"
                                                : "border-neutral-200 text-neutral-600"
                                        }`}
                                        data-testid={`inv-featured-${p.slug}`}
                                    >
                                        {featured ? "featured" : "—"}
                                    </button>
                                </div>
                                <div className="col-span-2 lg:col-span-1 flex flex-col gap-1">
                                    <button
                                        type="button"
                                        onClick={() => save(p)}
                                        disabled={!dirty || savingId === p.id}
                                        className="inline-flex items-center justify-center gap-2 bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-white font-bold uppercase tracking-[0.18em] text-[10px] px-3 py-2 transition-colors"
                                        data-testid={`inv-save-${p.slug}`}
                                    >
                                        {savedId === p.id ? (
                                            <>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> saved
                                            </>
                                        ) : (
                                            <>
                                                <Save className="w-3.5 h-3.5" /> {savingId === p.id ? "saving…" : "save"}
                                            </>
                                        )}
                                    </button>
                                    {pricing && Object.keys(pricing).length > 0 && (
                                        <DosePricingEditor
                                            pricing={pricing}
                                            onChange={(np) => stage(p.id, { pricing: np })}
                                            testidPrefix={`inv-dose-${p.slug}`}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function DosePricingEditor({ pricing, onChange, testidPrefix }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full text-[10px] uppercase tracking-[0.18em] text-neutral-600 hover:text-black border border-neutral-200 rounded-lg px-3 py-2"
                data-testid={`${testidPrefix}-toggle`}
            >
                {open ? "− doses" : `+ doses (${Object.keys(pricing).length})`}
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-64 bg-[#f8f8f8] border border-neutral-200 rounded-lg p-3 z-20 space-y-2 shadow-2xl">
                    {Object.entries(pricing).map(([dose, value]) => (
                        <div key={dose} className="flex items-center gap-2 text-xs">
                            <span className="font-mono text-[#0077b6] w-12">{dose}</span>
                            <span className="text-neutral-500">$</span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={value}
                                onChange={(e) =>
                                    onChange({ ...pricing, [dose]: parseFloat(e.target.value) || 0 })
                                }
                                className="flex-1 bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded px-2 py-1 font-mono text-xs"
                                data-testid={`${testidPrefix}-${dose}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
